/*
 * Conversation import utilities.
 * Supports JSON format with validation and conflict resolution.
 */

import { saveConversation, getIndex, saveIndex } from "./store.js";

/**
 * Import a conversation from JSON file.
 * @param {File} file - File object from file input
 * @returns {Promise<Object>} Imported conversation object
 */
export async function importConversationJSON(file) {
  const text = await file.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error("Invalid JSON file");
  }

  // Validate structure
  if (!data.conversation) {
    throw new Error("Invalid conversation format: missing conversation object");
  }

  if (!data.conversation.messages || !Array.isArray(data.conversation.messages)) {
    throw new Error("Invalid conversation format: missing or invalid messages array");
  }

  // Check for version compatibility
  if (data.version && data.version > 1) {
    console.warn("Conversation was exported with a newer version, some features may not be supported");
  }

  // Size check (warn if > 5MB)
  if (text.length > 5 * 1024 * 1024) {
    console.warn("Large conversation file imported (>5MB)");
  }

  // Create new conversation with imported data
  const conv = {
    ...data.conversation,
    id: "conv_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9),
    imported: true,
    importedAt: new Date().toISOString(),
    name: (data.conversation.name || "Imported") + " (imported)",
  };

  // Validate and sanitize messages
  conv.messages = conv.messages.filter((msg) => {
    return msg && typeof msg === "object" && ["user", "assistant", "tool", "system"].includes(msg.role);
  });

  conv.messageCount = conv.messages.length;
  conv.updatedAt = new Date().toISOString();

  // Save conversation
  await saveConversation(conv);

  // Update index
  const index = await getIndex();
  index.conversations.push({
    id: conv.id,
    name: conv.name,
    createdAt: conv.createdAt || new Date().toISOString(),
    updatedAt: conv.updatedAt,
    messageCount: conv.messageCount,
    imported: true,
  });
  await saveIndex(index);

  return conv;
}

/**
 * Import multiple conversations from a single JSON file.
 * @param {File} file - File object containing array of conversations
 * @returns {Promise<Array>} Array of imported conversations
 */
export async function importMultipleConversations(file) {
  const text = await file.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error("Invalid JSON file");
  }

  // Check if it's a batch export
  if (!data.conversations || !Array.isArray(data.conversations)) {
    throw new Error("Invalid batch format: missing conversations array");
  }

  const imported = [];
  const index = await getIndex();

  for (const convData of data.conversations) {
    const conv = {
      ...convData,
      id: "conv_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9),
      imported: true,
      importedAt: new Date().toISOString(),
      name: (convData.name || "Imported") + " (imported)",
    };

    // Validate messages
    if (conv.messages && Array.isArray(conv.messages)) {
      conv.messages = conv.messages.filter((msg) => {
        return msg && typeof msg === "object" && ["user", "assistant", "tool", "system"].includes(msg.role);
      });
      conv.messageCount = conv.messages.length;
    } else {
      conv.messages = [];
      conv.messageCount = 0;
    }

    await saveConversation(conv);

    index.conversations.push({
      id: conv.id,
      name: conv.name,
      createdAt: conv.createdAt || new Date().toISOString(),
      updatedAt: conv.updatedAt,
      messageCount: conv.messageCount,
      imported: true,
    });

    imported.push(conv);
  }

  await saveIndex(index);
  return imported;
}

/**
 * Validate a conversation object.
 * @param {Object} conv - Conversation object to validate
 * @returns {Object} Validation result with valid flag and errors
 */
export function validateConversation(conv) {
  const errors = [];

  if (!conv) {
    errors.push("Conversation object is null or undefined");
    return { valid: false, errors };
  }

  if (!conv.id) {
    errors.push("Missing conversation ID");
  }

  if (!conv.messages || !Array.isArray(conv.messages)) {
    errors.push("Missing or invalid messages array");
  } else {
    conv.messages.forEach((msg, idx) => {
      if (!msg.role) {
        errors.push(`Message at index ${idx} missing role`);
      } else if (!["user", "assistant", "tool", "system"].includes(msg.role)) {
        errors.push(`Message at index ${idx} has invalid role: ${msg.role}`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
